import React, { useState, useEffect } from 'react';
import { api } from '../api';

const BRAND_DEPTS = ['KIA CARS', 'SUZUKI CARS', 'GWM CARS', 'GAC CARS'];

const DEPT_COLORS = {
  'KIA CARS':    { bg: '#e0f0ff', color: '#1d6fa4', border: '#1d6fa440' },
  'SUZUKI CARS': { bg: '#ffe0e0', color: '#b91c1c', border: '#b91c1c40' },
  'GWM CARS':    { bg: '#fff0d0', color: '#b45309', border: '#b4530940' },
  'GAC CARS':    { bg: '#ffede0', color: '#c2410c', border: '#c2410c40' },
};

export default function FormSettingsPanel() {
  const [models, setModels]       = useState({}); // { 'KIA CARS': [{id, model}], ... }
  const [activeDept, setActiveDept] = useState('KIA CARS');
  const [newModel, setNewModel]   = useState('');
  const [adding, setAdding]       = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await api.getCarModels();
      setModels(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function handleAdd() {
    if (!newModel.trim()) return;
    setAdding(true); setError('');
    try {
      await api.addCarModel({ department: activeDept, model: newModel.trim() });
      setNewModel('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setAdding(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this model from the list?')) return;
    setError('');
    try {
      await api.deleteCarModel(id);
      await load();
    } catch (e) { setError(e.message); }
  }

  async function handleRename() {
    if (!editValue.trim()) return;
    setError('');
    try {
      await api.updateCarModel(editingId, editValue.trim());
      setEditingId(null);
      setEditValue('');
      await load();
    } catch (e) { setError(e.message); }
  }

  const deptModels = models[activeDept] || [];

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px' }}>Form Settings</h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
          Manage the options that appear in the job submission form used by contractors.
        </p>
      </div>

      {/* ── Section 1: Car Models ── */}
      <div className="card" style={{ marginBottom: 20, borderTop: '3px solid #4A7A91' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>🚗 Car Models</h3>
        <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 13 }}>
          These are the model options contractors see when submitting a job.
          USED CARS and PRIVATE CAR always use free-text entry.
        </p>

        {/* Brand tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {BRAND_DEPTS.map(dept => {
            const c = DEPT_COLORS[dept];
            const isActive = activeDept === dept;
            const count = (models[dept] || []).length;
            return (
              <button
                key={dept}
                onClick={() => { setActiveDept(dept); setNewModel(''); setError(''); setEditingId(null); }}
                style={{
                  padding: '7px 16px', borderRadius: 0, fontSize: 13, fontWeight: 700,
                  border: `2px solid ${isActive ? c.color : '#e5e7eb'}`,
                  background: isActive ? c.bg : 'white',
                  color: isActive ? c.color : '#6b7280',
                  cursor: 'pointer',
                }}
              >
                {dept}
                <span style={{
                  marginLeft: 6, fontSize: 11, fontWeight: 600,
                  background: isActive ? c.color : '#e5e7eb',
                  color: isActive ? 'white' : '#6b7280',
                  borderRadius: 0, padding: '1px 6px',
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 0, padding: '8px 12px', fontSize: 13, color: '#dc2626', marginBottom: 12 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: '#9ca3af', padding: '20px 0', textAlign: 'center' }}>Loading…</div>
        ) : (
          <>
            {/* Model list */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 0, overflow: 'hidden', marginBottom: 12 }}>
              {deptModels.length === 0 ? (
                <div style={{ padding: '20px 16px', color: '#9ca3af', textAlign: 'center', fontSize: 14 }}>
                  No models yet — add one below.
                </div>
              ) : deptModels.map((m, i) => {
                const c = DEPT_COLORS[activeDept];
                const isEditing = editingId === m.id;
                return (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px',
                      borderBottom: i < deptModels.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: isEditing ? '#f0f9ff' : 'white',
                    }}
                  >
                    {/* Drag handle visual (decorative) */}
                    <span style={{ color: '#d1d5db', fontSize: 14, cursor: 'default', userSelect: 'none' }}>⋮⋮</span>

                    {isEditing ? (
                      <>
                        <input
                          autoFocus
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditingId(null); }}
                          style={{
                            flex: 1, border: '1.5px solid #4A7A91', borderRadius: 0,
                            padding: '4px 8px', fontSize: 14, outline: 'none',
                          }}
                        />
                        <button onClick={handleRename}
                          style={{ background: '#4A7A91', color: 'white', border: 'none', borderRadius: 0, padding: '4px 10px', fontSize: 13, cursor: 'pointer' }}>
                          ✓ Save
                        </button>
                        <button onClick={() => setEditingId(null)}
                          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 0, padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: '#6b7280' }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{
                          flex: 1, fontSize: 14, fontWeight: 500,
                          color: c.color, background: c.bg,
                          padding: '3px 10px', borderRadius: 0, display: 'inline-block',
                        }}>
                          {m.model}
                        </span>
                        <button
                          onClick={() => { setEditingId(m.id); setEditValue(m.model); }}
                          title="Rename"
                          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 0, padding: '3px 8px', fontSize: 12, cursor: 'pointer', color: '#4A7A91' }}>
                          ✏
                        </button>
                        <button
                          onClick={() => handleDelete(m.id)}
                          title="Remove"
                          style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 0, padding: '3px 8px', fontSize: 12, cursor: 'pointer', color: '#dc2626' }}>
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add new model */}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={newModel}
                onChange={e => setNewModel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder={`Add new ${activeDept} model…`}
                style={{
                  flex: 1, padding: '9px 12px', border: '1.5px solid #e5e7eb',
                  borderRadius: 0, fontSize: 14, outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = '#4A7A91'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                onClick={handleAdd}
                disabled={adding || !newModel.trim()}
                className="btn btn-primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                {adding ? 'Adding…' : '+ Add Model'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Section 2: Staff / Contractors ── */}
      <div className="card" style={{ borderTop: '3px solid #F5C518' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>👷 Staff — "Detailing by"</h3>
        <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 13 }}>
          The list of contractors in the "Detailing by" step comes from the active contractors in the system.
          To add or remove a person, go to the <strong>Contractors</strong> tab.
        </p>
        <div style={{ background: '#fffbeb', border: '1.5px solid #F5C518', borderRadius: 0, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>💡</span>
          <div style={{ fontSize: 13, color: '#92400e' }}>
            <strong>To add a new team member</strong> to the form: go to the <em>Contractors</em> tab in the top menu → click <em>Add Contractor</em>.<br />
            <strong>To remove someone</strong>: in the Contractors tab, set their status to <em>Inactive</em> — they'll disappear from the form automatically.
          </div>
        </div>
      </div>
    </div>
  );
}
